// ============================================================================
// eventBusHub.ts — ЕДИНАЯ подписка на EventBus сервера (SRP).
// Один долгий стрим на ВСЁ приложение (топик '#'), события раздаются локальным
// подписчикам. Причина: браузер держит ограниченное число соединений на домен
// (HTTP/1.1 ≈ 6) — каждый отдельный стрим занимал соединение, и unary-запросы
// (LogEvent и др.) вставали в очередь на соединение → таймаут 20с → abort.
// Стрим переподключается сам (1с), разрывы НЕ пишутся в журнал (норма сети).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { createLogger } from '@/lib/logger';
import { isTransportError } from '@/lib/errors';

const log = createLogger('eventBusHub');

/** Watchdog пульса: нет данных от сервера дольше этого — TCP мёртв (nginx/NAT
 *  рвут тихо), принудительный разрыв и переподключение. Сервер шлёт ping
 *  каждые 15с — порог = 3 интервала. */
const PING_TIMEOUT_MS = 45000;

/** Префикс базы в теме шины (сервер: base.<slug>.<исходная тема>). */
const BASE_TOPIC_RE = /^base\.([^.]+)\.(.+)$/;

function splitBaseTopic(topic: string): { base: string; topic: string } | null {
  const m = BASE_TOPIC_RE.exec(topic);
  if (!m) return null;
  return { base: m[1], topic: m[2] };
}

export interface BusEvent {
  topic: string;
  entityId?: string;
  entityType?: string;
  payload: any; // распарсенный JSON (null, если payload пуст)
  rawPayload: string;
  /** source: 'server' (пришло по стриму EventBus) | 'local' (опубликовано
   *  приложением через eventBusHub.emit). Observer на шине различает их. */
  source?: 'server' | 'local';
}

type BusHandler = (ev: BusEvent) => void;
/** online — стрим поднялся; offline — транспортный сбой (сервер не отвечает);
 *  reconnecting — разрыв без признаков недоступности (рестарт и т.п.). */
type StatusHandler = (status: 'online' | 'reconnecting' | 'offline') => void;

class EventBusHub {
  private handlers = new Set<BusHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private started = false;
  private enabled = false;
  private reconnectDelay = 1000;
  private stalled = false; // пауза из-за перманентной ошибки (не сетевой)
  private attempts = 0;
  private running = false; // один цикл стрима на приложение (иначе дубли доставки)
  private poked = false; // «разбудить» спящий цикл (poke → не создавать второй)
  private streamBase = ''; // база, на которую подписан ТЕКУЩИЙ стрим
  private activeAbort: AbortController | null = null;
  private baseWatch: (() => void) | null = null;

  /** Перманентные ошибки — НЕ сетевые: повторять их мгновенно бессмысленно
   *  (404 «base not found», 401, 403, 400 и т.п.) → пауза 30с (стоп-условие
   *  для лавин ретраев → утечек памяти/CPU). Ретработаем только сеть/5xx. */
  private isRetryable(err: unknown): boolean {
    const raw = String((err as any)?.rawMessage || (err as any)?.message || err || '');
    if (/failed to fetch|networkerror|econnrefused|econnreset|timeout|aborted|unavailable|closed|socket hang up|network is unreachable/i.test(raw)) {
      return true;
    }
    const code = (err as any)?.code;
    // connect-es коды: перманентные (клиент/конфиг) — не ретраим бесконечно.
    const permanent = [1, 3, 5, 6, 7, 9, 11, 12, 16];
    if (typeof code === 'number' && permanent.includes(code)) return false;
    if (typeof code === 'string' && /^[3579]|notfound|invalid_argument|permission|unauthenticated|unauth|failed_precondition|unimplemented/i.test(code)) {
      return false;
    }
    return true; // сетевые/5xx — обычный backoff
  }

  private logReconnect(e: unknown, action: string): void {
    const cs = useCenterStore.getState();
    this.attempts += 1;
    const detail = {
      attempt: this.attempts,
      centerUrl: cs.centerUrl || '(local)',
      baseSlug: cs.baseSlug || '(none)',
      code: (e as any)?.code ?? '',
      raw: String((e as any)?.rawMessage || (e as any)?.message || e).slice(0, 300),
    };
    log.warn(`event bus stream ${action}: ${JSON.stringify(detail)}`);
  }

  /** Подписка; возвращает функцию отписки. */
  subscribe(handler: BusHandler): () => void {
    this.handlers.add(handler);
    this.ensureStarted();
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** ЛОКАЛЬНАЯ публикация события (единая шина: всё — через неё). Синхронная,
   *  НЕ идёт на сервер (source='local'). Observer на шине видит и local, и
   *  server-события. Подписчики вызываются в порядке подписки. */
  emit(topic: string, payload?: any): void {
    const ev: BusEvent = {
      topic,
      entityId: payload?.entityId || '',
      entityType: payload?.entityType || '',
      payload: payload ?? null,
      rawPayload: payload !== undefined ? JSON.stringify(payload) : '',
      source: 'local',
    };
    for (const h of Array.from(this.handlers)) {
      try {
        h(ev);
      } catch (e) {
        log.warn('event bus local handler error', e);
      }
    }
  }

  /** Observer на шине: видит ВСЕ события (server + local) — для мониторинга
   *  и отладки. Фильтр по topic (полное/подстрока) опционален. */
  observe(handler: (ev: BusEvent) => void, topicFilter?: string): () => void {
    const wrapped: BusHandler = (ev) => {
      if (topicFilter && !ev.topic.includes(topicFilter)) return;
      try {
        handler(ev);
      } catch (e) {
        log.warn('event bus observer error', e);
      }
    };
    this.handlers.add(wrapped);
    return () => {
      this.handlers.delete(wrapped);
    };
  }

  /** Статус стрима (SSoT подключения): online — стрим жив; reconnecting — оборвался. */
  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  private emitStatus(s: 'online' | 'reconnecting' | 'offline'): void {
    for (const h of Array.from(this.statusHandlers)) {
      try {
        h(s);
      } catch (e) {
        log.warn('event bus status handler error', e);
      }
    }
  }

  /** Включить/выключить стрим (SSoT — main.tsx после входа/выхода):
   *  до входа шина не стартует (сервер отвечает 401/400 на Subscribe). */
  setEnabled(on: boolean): void {
    this.enabled = on;
    // Смена активной базы: стрим подписан на базу СЕРВЕРОМ (темы с префиксом)
    // — старую подписку закрываем, цикл переподключится уже к новой базе.
    if (on && !this.baseWatch) {
      this.baseWatch = useCenterStore.subscribe((state) => {
        const slug = state.baseSlug || '';
        if (this.running && slug !== this.streamBase) {
          log.debug(`active base changed (${this.streamBase || '—'} → ${slug || '—'}) — event stream restarts`);
          this.activeAbort?.abort();
        }
      });
    }
    if (on && !this.started) {
      this.stalled = false;
      void this.run();
      this.started = true;
    }
    if (!on) {
      this.started = false;
      this.stalled = false;
    }
  }

  /** Возобновить стрим немедленно (фокус окна/сеть вернулись) после stall. */
  poke(): void {
    if (!this.enabled) return;
    this.stalled = false;
    this.reconnectDelay = 1000;
    this.poked = true; // разбудить существующий цикл; второй run() НЕ создавать
    // Принудительный poke должен работать и для «зависшего» стрима, а не
    // только для stalled: закрываем текущий поток, цикл переподключится.
    this.activeAbort?.abort();
    log.debug('event bus poke: стрим перезапускается по событию окна/профиля');
    if (!this.running) void this.run();
  }

  /** Прерываемый сон: выходит при disable/poke (без второго цикла стрима). */
  private async sleepInterruptible(ms: number): Promise<void> {
    const step = 500;
    let waited = 0;
    while (waited < ms) {
      if (!this.enabled || this.poked) {
        this.poked = false;
        return;
      }
      await new Promise((r) => setTimeout(r, Math.min(step, ms - waited)));
      waited += step;
    }
  }

  private ensureStarted(): void {
    if (this.started) return;
    if (!this.enabled) return;
    this.started = true;
    void this.run();
  }

  private async run(): Promise<void> {
    if (this.running) return; // ЕДИНСТВЕННЫЙ цикл на приложение (дубли доставки невозможны)
    this.running = true;
    try {
      while (this.enabled) {
        const ac = new AbortController();
        this.activeAbort = ac;
        // Транспортный сбой (сервер не отвечает) — «offline» (офлайн-режим),
        // иначе (разрыв при живом сервере) — «reconnecting».
        let transportFailure = false;
        try {
          // ЖДАТЬ активную базу (базы-как-схемы): Subscribe без X-Hydra-Base
          // → 400 «no base selected». Ждём БЕЗ таймаута: база появится при
          // выборе (setBase) — стрим сразу пойдёт; enabled=false прерывает.
          let waited = 0;
          while (!useCenterStore.getState().baseSlug && this.enabled && waited < 30000) {
            await new Promise((r) => setTimeout(r, 100));
            waited += 100;
          }
          const activeBase = useCenterStore.getState().baseSlug || '';
          if (!activeBase || !this.enabled) {
            ac.abort();
            await this.sleepInterruptible(2000);
            continue;
          }
          this.streamBase = activeBase;
          this.attempts = 0;
          const res = await getSDK().eventBus.subscribe({ topics: ['#'] }, { signal: ac.signal });
          this.reconnectDelay = 1000;
          this.stalled = false;
          this.emitStatus('online');
          await this.pump(res, ac);
          this.logReconnect(new Error('stream ended normally'), 'ended');
        } catch (e) {
          this.logReconnect(e, 'error');
          if (!this.isRetryable(e)) {
            // Перманентная ошибка: не молотим (логи подробные — выше).
            // Пауза 30с; возобновление — по poke() (focus/online) или таймеру.
            this.stalled = true;
            ac.abort();
            this.emitStatus('reconnecting');
            await this.sleepInterruptible(30000);
            continue;
          }
          transportFailure = isTransportError(e);
        }
        if (!this.enabled) return;
        // Стрима больше нет (оборвался/закрылся/пульс пропал) — ЧЕСТНЫЙ статус:
        // зелёный «online» при мёртвом сервере — ложь индикатора (был баг).
        ac.abort();
        this.emitStatus(transportFailure ? 'offline' : 'reconnecting');
        // Backoff: 1с → 3с → 10с → 30с (максимум, jitter ±30%).
        await this.sleepInterruptible(this.reconnectDelay);
        this.reconnectDelay = Math.min(this.reconnectDelay * 3, 30000);
        // Jitter ±30% чтобы не сломать сервер одновременными reconnect всех клиентов.
        const jitter = this.reconnectDelay * 0.3 * (Math.random() * 2 - 1);
        await this.sleepInterruptible(Math.max(0, jitter));
      }
    } finally {
      this.running = false;
      this.activeAbort = null;
    }
  }

  /** Чтение стрима с watchdog пульса: нет данных PING_TIMEOUT_MS → разрыв. */
  private async pump(res: AsyncIterable<any>, ac: AbortController): Promise<void> {
    const iter = res[Symbol.asyncIterator]();
    let lastSeen = Date.now();
    while (true) {
      const remaining = Math.max(lastSeen + PING_TIMEOUT_MS - Date.now(), 1);
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeoutP = new Promise<'timeout'>((resolve) => {
        timer = setTimeout(() => resolve('timeout'), remaining);
      });
      let result: IteratorResult<any> | 'timeout';
      try {
        result = await Promise.race([iter.next(), timeoutP]);
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
      if (result === 'timeout') {
        log.warn('event bus idle watchdog fired — dead connection, reconnecting');
        return;
      }
      if (result.done) return;
      lastSeen = Date.now();
      const ev = result.value;
      const wireTopic: string = ev?.topic || '';
      if (wireTopic === '_ping') continue;
      // Клиент МОЛЧИТ вне базы и ИГНОРИРУЕТ чужую базу (двойная защита:
      // сервер уже ограничил подписку темой base.<slug>.*, плюс payload.base).
      const activeBase = useCenterStore.getState().baseSlug || '';
      if (!activeBase) continue;
      let topic = wireTopic;
      const scoped = splitBaseTopic(wireTopic);
      if (scoped) {
        if (scoped.base !== activeBase) continue;
        topic = scoped.topic; // дальше подписчики видят ЛОГИЧЕСКУЮ тему
      }
      let payload: any = null;
      try {
        payload = ev?.payload ? JSON.parse(ev.payload) : null;
      } catch {
        payload = null;
      }
      if (payload && typeof payload.base === 'string' && payload.base && payload.base !== activeBase) {
        continue; // событие чужой базы (payload — вторая линия защиты)
      }
      const busEvent: BusEvent = {
        topic,
        entityId: ev?.entityId || '',
        entityType: ev?.entityType || '',
        payload,
        rawPayload: ev?.payload || '',
        source: 'server',
      };
      for (const h of Array.from(this.handlers)) {
        try {
          h(busEvent);
        } catch (e) {
          log.warn('event bus handler error', e);
        }
      }
    }
  }
}

export const eventBusHub = new EventBusHub();
